"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.PropertyParser = exports.AltiumPropertiesSchema = exports.AltiumPropertySchema = void 0;
const zod_1 = require("zod");
const sigma_engine_shared_1 = require("@cadstrom/sigma-engine-shared");
const Util_1 = require("../../../../Util");
const ModelText_1 = require("../../../../../ModelText");
exports.AltiumPropertySchema = zod_1.default.object({
    key: zod_1.default.string(),
    value: zod_1.default.string(),
});
exports.AltiumPropertiesSchema = zod_1.default.record(zod_1.default.string(), exports.AltiumPropertySchema.optional());
class PropertyParser {
    static parseProperties(data) {
        if (data.length === 0) {
            return {};
        }
        if (data.length >= 2 && data[0] === 0xff && data[1] === 0xfe) {
            let cleanData = data.subarray(2);
            if (cleanData.length >= 2 && cleanData[cleanData.length - 2] === 0 && cleanData[cleanData.length - 1] === 0) {
                cleanData = cleanData.subarray(0, -2);
            }
            return this.parsePropertyString(new TextDecoder('utf-16le').decode(cleanData));
        }
        let cleanData = data;
        if (data[data.length - 1] === 0) {
            cleanData = data.subarray(0, -1);
        }
        const utf8Prefix = '%UTF8%';
        if (cleanData.length >= utf8Prefix.length &&
            cleanData[0] === 0x25 &&
            (0, Util_1.bytesToLatin1)(cleanData.subarray(0, utf8Prefix.length)) === utf8Prefix) {
            return this.parsePropertyString(new TextDecoder('utf-8').decode(cleanData.subarray(utf8Prefix.length)));
        }
        return this.parsePropertyPairs((0, Util_1.bytesToLatin1)(cleanData));
    }
    static parsePropertyPairs(byteLine) {
        const properties = {};
        const lockedByUtf8 = new Set();
        const utf8Prefix = '%UTF8%';
        for (const pair of this.splitPropertyPairs(byteLine)) {
            const equalIndex = pair.indexOf('=');
            let key = equalIndex === -1 ? pair : pair.substring(0, equalIndex);
            const rawValue = equalIndex === -1 ? '' : pair.substring(equalIndex + 1);
            const isUtf8 = key.startsWith(utf8Prefix);
            if (isUtf8) {
                key = key.substring(utf8Prefix.length);
            }
            const normalizedKey = key.trim().toLowerCase();
            if (normalizedKey.length === 0) {
                continue;
            }
            const value = isUtf8 ? new TextDecoder('utf-8').decode(this.byteStringToBytes(rawValue)) : (0, Util_1.latin1ToWindows1252)(rawValue);
            if (isUtf8) {
                const representable = (0, Util_1.isWindows1252Representable)(value);
                if (representable || properties[normalizedKey] === undefined) {
                    properties[normalizedKey] = { key, value };
                }
                if (representable) {
                    lockedByUtf8.add(normalizedKey);
                }
            }
            else if (!lockedByUtf8.has(normalizedKey)) {
                properties[normalizedKey] = { key, value };
            }
        }
        return properties;
    }
    static byteStringToBytes(byteString) {
        const bytes = new Uint8Array(byteString.length);
        for (let i = 0; i < byteString.length; i++) {
            bytes[i] = byteString.charCodeAt(i) & 0xff;
        }
        return bytes;
    }
    static parsePropertyString(text) {
        const properties = {};
        if (text.length === 0) {
            return properties;
        }
        const pairs = this.splitPropertyPairs(text);
        for (const pair of pairs) {
            const equalIndex = pair.indexOf('=');
            if (equalIndex === -1) {
                const key = pair;
                if (key.length > 0) {
                    properties[key.trim().toLowerCase()] = { key, value: '' };
                }
                continue;
            }
            const key = pair.substring(0, equalIndex);
            const value = pair.substring(equalIndex + 1);
            if (key.length > 0) {
                properties[key.trim().toLowerCase()] = { key, value };
            }
        }
        return properties;
    }
    static convertTildeOneToNewline(text) {
        return text.replace(/~1/g, '\n');
    }
    static splitPropertyPairs(text) {
        const pairs = [];
        let start = 0;
        let continuedPair = '';
        let pipe = text.indexOf('|');
        while (pipe !== -1) {
            if (text[pipe + 1] === '>') {
                continuedPair += text.substring(start, pipe) + '\n';
                start = pipe + 2;
            }
            else {
                const pair = continuedPair + text.substring(start, pipe);
                if (pair.length > 0) {
                    pairs.push(pair);
                }
                continuedPair = '';
                start = pipe + 1;
            }
            pipe = text.indexOf('|', start);
        }
        const finalPair = continuedPair + text.substring(start);
        if (finalPair.length > 0) {
            pairs.push(finalPair);
        }
        return pairs;
    }
    static parsePropertyLength(lengthRaw) {
        const isBinary = (lengthRaw & 0xff000000) !== 0;
        const length = lengthRaw & 0x00ffffff;
        return { isBinary, length };
    }
    static parseAltiumTextTransforms(text) {
        const isNetRef = text.value instanceof sigma_engine_shared_1.NetRef;
        const encoded = this.encodeAltiumDisplayText(isNetRef ? String(text.value) : text.value);
        if (isNetRef) {
            text.value.setName(encoded.value);
        }
        else {
            text.value = encoded.value;
        }
        if (encoded.textTransforms) {
            text.textTransforms = [...(text.textTransforms ?? []), ...encoded.textTransforms];
        }
        return text;
    }
    static encodeAltiumDisplayText(value) {
        const encoded = (0, ModelText_1.encodeStrippedText)({
            raw: this.convertTildeOneToNewline(value),
            dialects: [ModelText_1.TextDialect.Altium],
        });
        return encoded.textTransforms ? encoded : { value: encoded.value };
    }
}
exports.PropertyParser = PropertyParser;
//# sourceMappingURL=PropertyParser.js.map